import { createContext, PropsWithChildren, useCallback, useContext } from "react";

import { OrganizationRoutes } from "@simplyk/common";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";

import { EmailEditorDrawer } from "@/components/EmailEditor/EmailEditorDrawer";

interface EmailEditorContextProps {
  openEmailEditorDrawer: (emailContentId: string, thankYouTransactionId?: string) => void;
  closeEmailEditorDrawer: () => void;
}

const EmailEditorContext = createContext<EmailEditorContextProps>({
  closeEmailEditorDrawer: () => {},
  openEmailEditorDrawer: () => {},
});

export const EmailEditorProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const params = useSearchParams();
  const query = Object.fromEntries(params.entries());

  const emailContentId = params.get("emailContentId") as string;
  const pathname = router.pathname;
  const isBuilderFlow = pathname.includes(OrganizationRoutes.FullScreenEmailEditor);
  const isEmailEditorOpen = Boolean(emailContentId) && !isBuilderFlow;

  const openEmailEditorDrawer = async (emailContentId: string, thankYouTransactionId?: string) => {
    router.push({
      query: { ...query, emailContentId, ...(thankYouTransactionId ? { thankYouTransactionId } : {}) },
    });
  };

  const closeEmailEditorDrawer = useCallback(() => {
    const { emailContentId: _emailContentId, thankYouTransactionId: _thankYouTransactionId, ...newQuery } = query;
    router.push({ query: newQuery });
  }, [query, router]);

  return (
    <EmailEditorContext.Provider value={{ openEmailEditorDrawer, closeEmailEditorDrawer }}>
      {children}
      <EmailEditorDrawer
        emailContentId={emailContentId}
        closeEmailEditor={closeEmailEditorDrawer}
        isOpen={isEmailEditorOpen}
      />
    </EmailEditorContext.Provider>
  );
};

export const useEmailEditorContext = () => useContext(EmailEditorContext);
